(() => {
  if (window.feGloboBundleBridgeInstalled) return;
  window.feGloboBundleBridgeInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  const cartAddPath = () => {
    try {
      return new URL(theme?.routes?.cart_add_url || `${Shopify?.routes?.root || '/'}cart/add.js`, window.location.origin).pathname.replace(/\.js$/, '');
    } catch (_) {
      return '/cart/add';
    }
  };

  let pending = null;
  let syncInFlight = false;
  const PENDING_TTL = 15000;

  const now = () => Date.now();
  const isFresh = (record) => record && now() - record.createdAt <= PENDING_TTL;

  const isGloboForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const root = form.closest('product-info');
    return !!(form.querySelector('.gpo-hidden-inputs') || root?.querySelector('.gpo-app'));
  };

  const bundleDetailsForForm = (form) => {
    const formId = form?.id;
    if (!formId) return null;
    return Array.from(document.querySelectorAll('details[is="product-bundle-details"][form]'))
      .find((element) => element.getAttribute('form') === formId) || null;
  };

  const selectedBundles = (form) => {
    const details = bundleDetailsForForm(form);
    if (!details) return [];
    const seen = new Set();
    return Array.from(details.querySelectorAll('input[name="bundles"]:checked:not(:disabled)'))
      .map((input) => String(input.value || ''))
      .filter((id) => /^\d+$/.test(id) && !seen.has(id) && seen.add(id));
  };

  const scrubLegacyBundleRows = (formData, variantIds) => {
    if (!variantIds.length) return;
    const selected = new Set(variantIds.map(String));
    const indexes = new Set();

    for (const [key, value] of Array.from(formData.entries())) {
      const match = key.match(/^items\[(\d+)\]\[id\]$/);
      if (!match) continue;
      const index = Number(match[1]);
      if (index >= 90 && selected.has(String(value))) indexes.add(match[1]);
    }

    if (!indexes.size) return;
    for (const key of Array.from(formData.keys())) {
      for (const index of indexes) {
        if (key.startsWith(`items[${index}]`)) {
          formData.delete(key);
          break;
        }
      }
    }
  };

  const sanitizeCartAddBody = (body, variantIds) => {
    if (!body || !variantIds.length) return body;
    if (body instanceof FormData) {
      scrubLegacyBundleRows(body, variantIds);
      return body;
    }
    if (typeof body !== 'string') return body;
    try {
      const payload = JSON.parse(body);
      if (!Array.isArray(payload?.items)) return body;
      const selected = new Set(variantIds.map(String));
      payload.items = payload.items.filter((item) => !selected.has(String(item?.id ?? '')));
      return JSON.stringify(payload);
    } catch (_) {
      return body;
    }
  };

  const setPending = (form, variantIds) => {
    pending = variantIds.length ? {
      form,
      details: bundleDetailsForForm(form),
      variantIds,
      createdAt: now()
    } : null;
  };

  const takePending = () => {
    if (!isFresh(pending)) {
      pending = null;
      return null;
    }
    return pending;
  };

  const isCartAddUrl = (input) => {
    let raw = '';
    if (typeof input === 'string') raw = input;
    else if (input instanceof URL) raw = input.href;
    else if (input && typeof input.url === 'string') raw = input.url;
    if (!raw) return false;

    try {
      const url = new URL(raw, window.location.origin);
      return url.origin === window.location.origin && url.pathname.replace(/\.js$/, '') === cartAddPath();
    } catch (_) {
      return false;
    }
  };

  const responseSucceeded = async (response) => {
    if (!response || !response.ok) return false;
    try {
      const payload = await response.clone().json();
      return !(payload && (payload.status >= 400 || payload.errors || payload.message === 'Cart Error'));
    } catch (_) {
      return true;
    }
  };

  const setSyncing = (active) => {
    syncInFlight = active;
    document.documentElement.toggleAttribute('data-fe-bundle-syncing', active);
  };

  const messageFor = (kind) => {
    const de = (document.documentElement.lang || '').toLowerCase().startsWith('de');
    if (kind === 'sync') return de ? 'Zusatz wird gerade zum Warenkorb hinzugefügt.' : 'Your extra is being added to the cart.';
    return de
      ? 'Das Hauptprodukt wurde hinzugefügt, aber ein gewähltes Extra konnte nicht hinzugefügt werden. Bitte prüfe den Warenkorb, bevor du zur Kasse gehst.'
      : 'The main product was added, but a selected extra could not be added. Please check the cart before checkout.';
  };

  const showError = (record) => {
    const box = record?.form?.querySelector('.product-form__error-message');
    if (box) {
      box.textContent = messageFor('error');
      box.hidden = false;
    }
    document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
      detail: { errorMessage: messageFor('error') }
    }));
  };

  const clearSelectedBundles = (record) => {
    if (!record?.details) return;
    record.details.querySelectorAll('input[name="bundles"]:checked').forEach((input) => {
      input.checked = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    const standard = record.details.querySelector('[data-fe-no-packaging]');
    if (standard) standard.checked = true;
  };

  const addBundles = async (record) => {
    if (!record?.variantIds?.length) return true;
    setSyncing(true);
    try {
      const response = await nativeFetch(theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: record.variantIds.map((id) => ({ id: Number(id), quantity: 1 }))
        })
      });
      if (!(await responseSucceeded(response))) throw new Error('Bundle add failed');
      clearSelectedBundles(record);
      window.setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      }, 0);
      return true;
    } catch (error) {
      console.error('[ForeverEngraved] Extra sync failed', error);
      showError(record);
      return false;
    } finally {
      pending = null;
      setSyncing(false);
    }
  };

  document.addEventListener('formdata', (event) => {
    const form = event.target;
    if (!isGloboForm(form)) return;
    const variants = selectedBundles(form);
    scrubLegacyBundleRows(event.formData, variants);
    setPending(form, variants);
  }, true);

  window.fetch = async function(input, init) {
    const record = isCartAddUrl(input) ? takePending() : null;
    const safeInit = record && init ? { ...init, body: sanitizeCartAddBody(init.body, record.variantIds) } : init;
    let response;
    try {
      response = await nativeFetch(input, safeInit);
    } catch (error) {
      if (record) pending = null;
      throw error;
    }
    if (!record) return response;

    if (await responseSucceeded(response)) {
      await addBundles(record);
    } else {
      pending = null;
    }
    return response;
  };

  if (window.XMLHttpRequest?.prototype) {
    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.__feCartAdd = isCartAddUrl(url);
      return nativeOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function(body) {
      const record = this.__feCartAdd ? takePending() : null;
      if (record) {
        setSyncing(true);
        this.addEventListener('loadend', async () => {
          try {
            const ok = this.status >= 200 && this.status < 300;
            if (ok) await addBundles(record);
            else pending = null;
          } finally {
            setSyncing(false);
          }
        }, { once: true });
      }
      try {
        return nativeSend.call(this, record ? sanitizeCartAddBody(body, record.variantIds) : body);
      } catch (error) {
        if (record) {
          pending = null;
          setSyncing(false);
        }
        throw error;
      }
    };
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input?.matches) return;
    const details = input.closest?.('details[is="product-bundle-details"]');
    if (!details) return;

    if (input.matches('input[name="bundles"][data-fe-packaging]') && input.checked) {
      details.querySelectorAll('input[name="bundles"][data-fe-packaging]:checked').forEach((other) => {
        if (other === input) return;
        other.checked = false;
        other.dispatchEvent(new Event('change', { bubbles: true }));
      });
      const standard = details.querySelector('[data-fe-no-packaging]');
      if (standard) standard.checked = false;
    } else if (input.matches('[data-fe-no-packaging]') && input.checked) {
      details.querySelectorAll('input[name="bundles"][data-fe-packaging]:checked').forEach((box) => {
        box.checked = false;
        box.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (!syncInFlight) return;
    const checkout = event.target.closest('a[href*="/checkout"],button[name="checkout"],input[name="checkout"],[data-checkout]');
    if (!checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const live = document.querySelector('[data-fe-bundle-sync-status]') || document.createElement('div');
    if (!live.isConnected) {
      live.dataset.feBundleSyncStatus = 'true';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.style.cssText = 'position:fixed;left:50%;bottom:24px;z-index:99999;transform:translateX(-50%);max-width:min(92vw,520px);padding:12px 16px;border-radius:12px;background:#111;color:#fff;font:600 14px/1.35 system-ui,sans-serif;box-shadow:0 8px 30px rgba(0,0,0,.25)';
      document.body.append(live);
    }
    live.textContent = messageFor('sync');
    window.setTimeout(() => live.remove(), 1800);
  }, true);
})();
